import { useEffect, useRef, useState, Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from '@studio-freight/lenis';
import { Toaster } from "./components/ui/toaster";
import { SEO } from "./components/SEO";
import { CookieConsent } from "./components/CookieConsent";
import { faqData } from "./data/faqData";

// Components - Navigation and HeroSection stay eager (needed immediately
// for first paint / LCP). Everything below the fold is code-split into
// its own chunk, same pattern as the route-level splitting below, so
// main.js doesn't ship Radix UI, all the section components, and their
// content upfront for a page most visitors haven't scrolled to yet.
import { Navigation } from "./components/Navigation";
import { HeroSection } from "./components/HeroSection";
const AboutSection = lazy(() =>
  import("./components/AboutSection").then((m) => ({ default: m.AboutSection }))
);
const ServicesSection = lazy(() =>
  import("./components/ServicesSection").then((m) => ({ default: m.ServicesSection }))
);
const MidPageCta = lazy(() =>
  import("./components/MidPageCta").then((m) => ({ default: m.MidPageCta }))
);
const ValuePropsSection = lazy(() =>
  import("./components/ValuePropsSection").then((m) => ({ default: m.ValuePropsSection }))
);
const SupportSection = lazy(() =>
  import("./components/SupportSection").then((m) => ({ default: m.SupportSection }))
);
const TechStackSection = lazy(() =>
  import("./components/TechStackSection").then((m) => ({ default: m.TechStackSection }))
);
const FullBleedMedia = lazy(() =>
  import("./components/FullBleedMedia").then((m) => ({ default: m.FullBleedMedia }))
);
const TrustedPartners = lazy(() =>
  import("./components/TrustedPartners").then((m) => ({ default: m.TrustedPartners }))
);
const TestimonialsSection = lazy(() =>
  import("./components/TestimonialsSection").then((m) => ({ default: m.TestimonialsSection }))
);
const PortfolioScroll = lazy(() =>
  import("./components/PortfolioScroll").then((m) => ({ default: m.PortfolioScroll }))
);
const FAQSection = lazy(() =>
  import("./components/FAQSection").then((m) => ({ default: m.FAQSection }))
);
const ContactSection = lazy(() =>
  import("./components/ContactSection").then((m) => ({ default: m.ContactSection }))
);
const Footer = lazy(() =>
  import("./components/Footer").then((m) => ({ default: m.Footer }))
);

// FAQPage JSON-LD generated directly from the same data that powers the
// visible <FAQSection>, so structured data always matches on-page content
// (Google's requirement for FAQ rich results, and generally good practice
// for AI answer engines that check for consistency).
const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "@id": "https://lolcorp.co.uk/#faq",
  mainEntity: faqData.map((item) => ({
    "@type": "Question",
    name: item.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: item.answer,
    },
  })),
};

// Pages - code-split so homepage doesn't ship their JS upfront
const ConsultationPage = lazy(() =>
  import("./pages/ConsultationPage").then((m) => ({ default: m.ConsultationPage }))
);
const InstantQuotePage = lazy(() =>
  import("./pages/InstantQuotePage").then((m) => ({ default: m.InstantQuotePage }))
);
const MaintenancePage = lazy(() =>
  import("./pages/MaintenancePage").then((m) => ({ default: m.MaintenancePage }))
);
const ReferralsPage = lazy(() =>
  import("./pages/ReferralsPage").then((m) => ({ default: m.ReferralsPage }))
);
const PortfolioPage = lazy(() =>
  import("./pages/PortfolioPage").then((m) => ({ default: m.PortfolioPage }))
);
// Chat widget pulls in socket.io-client - split out of the critical path
const ChatWidget = lazy(() => import("./components/ChatWidget"));

// Defers mounting expensive below-the-fold content until it's actually
// about to be scrolled into view. Specifically added for <FAQSection>:
// Radix UI's Collapsible (used by the accordion) measures every item's
// content height via getBoundingClientRect as soon as it MOUNTS -
// regardless of open/closed state or scroll position - which showed up as
// measurable forced-reflow time during initial page load in a Lighthouse
// audit. Framer Motion's whileInView/useInView doesn't prevent this, since
// it only gates the *animation*, not the DOM mount itself - the Accordion
// was still mounting (and Radix still measuring) immediately either way.
const LazyMount = ({ children, rootMargin = '600px' }) => {
  const ref = useRef(null);
  const [shouldMount, setShouldMount] = useState(false);

  useEffect(() => {
    if (shouldMount) return;
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldMount(true);
          observer.disconnect();
        }
      },
      { rootMargin }
    );
    observer.observe(el);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldMount]);

  return <div ref={ref}>{shouldMount ? children : null}</div>;
};

// gtag.js only fires a pageview automatically on the very first load.
// Since this is a client-routed SPA, we need to send one manually on every
// route change too, or every page after the first is invisible to GA.
// (Consent Mode still governs whether this actually gets stored - if the
// visitor hasn't granted analytics consent, gtag drops/models it, it
// doesn't silently bypass their choice.)
const AnalyticsPageviewTracker = () => {
  const location = useLocation();
  useEffect(() => {
    if (typeof window.gtag === "function") {
      window.gtag('event', 'page_view', {
        page_path: location.pathname + location.search,
        page_location: window.location.href,
        page_title: document.title,
      });
    }
  }, [location]);
  return null;
};

const HomePage = () => {
  return (
    <div data-testid="home-page">
      <SEO
        title="Custom Software Development UK | Bespoke Software Company | LOLCorp"
        description="LOLCorp is a UK-based custom software development company building bespoke software, CRMs, dashboards and internal systems from £5,000. Affordable, fixed-scope projects delivered in 4-6 weeks. Get an instant quote."
        path="/"
        jsonLd={faqJsonLd}
      />
      <Navigation />
      <main>
        <HeroSection />
        {/* Each section gets its OWN Suspense boundary now, not one
            shared boundary for all 11 - this is the actual fix for the
            "whole page pops in at once when I reach that section" bug.
            React's Suspense doesn't reveal siblings progressively by
            default: when multiple lazy components share one boundary,
            React waits for EVERY chunk in that boundary to finish
            downloading before rendering ANY of them. With all 11 of
            these sections previously sharing one boundary, the entire
            block from TrustedPartners through ContactSection rendered as
            nothing at all (fallback=null) until the slowest individual
            chunk finished - then all 11 sections mounted simultaneously,
            producing one large, sudden layout shift right as a scrolling
            user reached that point. Invisible on localhost (all chunks
            resolve near-instantly there); very visible on a real domain
            with genuine per-chunk network latency. Separate boundaries
            mean each section now renders independently, as soon as its
            own chunk is ready, rather than being bottlenecked by
            whichever one happens to be slowest. */}
        <Suspense fallback={null}>
          <TrustedPartners />
        </Suspense>
        <Suspense fallback={null}>
          <FullBleedMedia />
        </Suspense>
        <Suspense fallback={null}>
          <AboutSection />
        </Suspense>
        <Suspense fallback={null}>
          <ServicesSection />
        </Suspense>
        <Suspense fallback={null}>
          <MidPageCta />
        </Suspense>
        <Suspense fallback={null}>
          <TestimonialsSection />
        </Suspense>
        <Suspense fallback={null}>
          <PortfolioScroll />
        </Suspense>
        <Suspense fallback={null}>
          <ValuePropsSection />
        </Suspense>
        <Suspense fallback={null}>
          <SupportSection />
        </Suspense>
        <Suspense fallback={null}>
          <TechStackSection />
        </Suspense>
        <Suspense fallback={null}>
          <LazyMount>
            <FAQSection />
          </LazyMount>
        </Suspense>
        <Suspense fallback={null}>
          <ContactSection />
        </Suspense>
      </main>
      <Suspense fallback={null}>
        <Footer />
      </Suspense>
      <Suspense fallback={null}>
        <ChatWidget serverUrl="https://sales.loljs.com" />
      </Suspense>
    </div>
  );
};

function App() {
  useEffect(() => {
    // Initialize Lenis smooth scroll
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      direction: 'vertical',
      gestureDirection: 'vertical',
      smooth: true,
      smoothTouch: false,
      touchMultiplier: 2,
    });

    // Exposed globally so other components (PortfolioScroll's jump-to-item
    // dots, currently) can route programmatic scrolling through Lenis
    // instead of native window.scrollTo(). Lenis is the one actually
    // driving scroll on this site (it intercepts wheel input) - calling
    // native scrollTo while Lenis tracks its own separate internal
    // position causes them to desync, which was showing up as random
    // jumps to unexpected scroll positions.
    window.__lenis = lenis;

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    // Lenis calculates the page's scroll limits once, at init. Several
    // sections on this page mount asynchronously after that (lazy-loaded
    // route/section chunks), including PortfolioScroll's 800vh scroll
    // container - added well after Lenis already measured the page.
    // Lenis's own docs are explicit that lenis.resize() must be called
    // whenever content height changes, or its internal scroll-limit
    // tracking desyncs from the real page height - this was the other
    // concrete cause of the random-jump bug. A ResizeObserver on
    // <body> catches any height change automatically (lazy sections
    // mounting, images loading, fonts swapping) without needing to know
    // in advance which component will change the page's height.
    const resizeObserver = new ResizeObserver(() => {
      lenis.resize();
    });
    resizeObserver.observe(document.body);

    // Additional, deterministic resize checkpoint - the ResizeObserver
    // above is correct but is inherently reactive/batched by the
    // browser, and doesn't fully close the gap on a fast first scroll:
    // Lenis starts actively driving scroll immediately on init, while
    // images across the page (logos, the full-bleed photo, portfolio
    // images) are still loading for the first several seconds, growing
    // the real page height as they resolve. If someone scrolls quickly
    // enough in that window, Lenis can compute a scroll target against a
    // page height that's still out of date by the time the ResizeObserver
    // callback actually fires, which was showing up specifically as a
    // "snap back to top" reachable only on the very first scroll after
    // load (matches exactly - this stops being reproducible once
    // everything has finished loading once). `window.load` fires once
    // every resource, including images, has fully loaded - a clean,
    // guaranteed checkpoint to force one more resize() at, closing that
    // gap for the common case of scrolling after the page looks ready.
    const handleWindowLoad = () => lenis.resize();
    if (document.readyState === 'complete') {
      handleWindowLoad();
    } else {
      window.addEventListener('load', handleWindowLoad);
    }

    // Handle hash navigation for smooth scroll
    const handleHashChange = () => {
      const hash = window.location.hash;
      if (hash) {
        const element = document.querySelector(hash);
        if (element) {
          setTimeout(() => {
            lenis.scrollTo(element, { offset: -80 });
          }, 100);
        }
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    
    // Initial check for hash
    if (window.location.hash) {
      handleHashChange();
    }

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('load', handleWindowLoad);
      lenis.destroy();
      delete window.__lenis;
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  return (
    <div className="App">
      {/* Subtle grain texture overlay */}
      <div className="grain-overlay" />
      
      <BrowserRouter>
        <AnalyticsPageviewTracker />
        <Suspense fallback={null}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/consultation" element={<ConsultationPage />} />
            <Route path="/instant-quote" element={<InstantQuotePage />} />
            <Route path="/maintenance" element={<MaintenancePage />} />
            <Route path="/referrals" element={<ReferralsPage />} />
            <Route path="/portfolio" element={<PortfolioPage />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
      
      <Toaster />
      <CookieConsent />
    </div>
  );
}

export default App;
